import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNav } from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Spinner } from '../components/UI';
import logoBrandStarter from '../assets/logo_brandstarter.png';

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,500&family=Inter:wght@300;400;500;600;700&display=swap');
  :root { --g:#173B34; --o:#E4572E; --c:#F6F2EC; --d:#222222; --line:#EDE9E2; }

  .pr { font-family: 'Inter', sans-serif; background: var(--c); min-height: 100vh; color: var(--d); }

  /* NAV */
  .pr-nav { background: #fff; border-bottom: 1px solid var(--line); display: flex; align-items: center; justify-content: space-between; padding: 0 24px; height: 68px; position: sticky; top: 0; z-index: 100; }
  .pr-nav img { height: 44px; width: auto; cursor: pointer; }
  .pr-nav-right { display: flex; align-items: center; gap: 10px; }
  .pr-nav-back { background: transparent; border: 1.5px solid var(--line); color: var(--g); border-radius: 999px; padding: 8px 18px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; }

  /* HERO */
  .pr-hero { background: var(--g); padding: 32px 24px; position: relative; overflow: hidden; }
  .pr-hero::before { content: ''; position: absolute; top: -60px; right: -60px; width: 200px; height: 200px; border-radius: 50%; background: rgba(228,87,46,0.1); pointer-events: none; }
  .pr-avatar { width: 60px; height: 60px; border-radius: 50%; background: var(--o); display: flex; align-items: center; justify-content: center; font-family: 'Playfair Display', serif; font-weight: 700; font-size: 24px; color: #fff; margin-bottom: 12px; position: relative; z-index: 2; }
  .pr-hero-name { font-family: 'Playfair Display', serif; font-size: 22px; color: #fff; font-weight: 700; margin-bottom: 4px; position: relative; z-index: 2; }
  .pr-hero-email { font-size: 13px; color: rgba(246,242,236,0.45); position: relative; z-index: 2; }
  .pr-hero-badges { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; position: relative; z-index: 2; }
  .pr-badge { font-size: 11px; font-weight: 700; padding: 4px 12px; border-radius: 999px; }
  .pr-badge-green { background: rgba(255,255,255,0.1); color: rgba(246,242,236,0.7); border: 1px solid rgba(255,255,255,0.15); }
  .pr-badge-orange { background: rgba(228,87,46,0.2); color: var(--o); border: 1px solid rgba(228,87,46,0.35); }
  .pr-badge-gold { background: rgba(212,175,55,0.15); color: #D4AF37; border: 1px solid rgba(212,175,55,0.3); }

  /* BODY */
  .pr-body { max-width: 720px; margin: 0 auto; padding: 28px 24px 60px; }

  /* TABS */
  .pr-tabs { display: flex; gap: 4px; background: #fff; border: 1px solid var(--line); border-radius: 12px; padding: 4px; margin-bottom: 24px; flex-wrap: wrap; }
  .pr-tab { flex: 1; min-width: 0; padding: 9px 12px; border: none; border-radius: 9px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; color: #888; background: transparent; transition: all .15s; white-space: nowrap; text-align: center; }
  .pr-tab.active { background: var(--g); color: #fff; }

  /* CARD */
  .pr-card { background: #fff; border: 1px solid var(--line); border-radius: 16px; padding: 24px; margin-bottom: 16px; }
  .pr-card-title { font-family: 'Playfair Display', serif; font-size: 17px; color: var(--g); font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }

  /* INFOS */
  .pr-info-row { display: flex; justify-content: space-between; align-items: center; padding: 11px 0; border-bottom: 1px solid var(--line); font-size: 14px; }
  .pr-info-row:last-child { border-bottom: none; }
  .pr-info-lbl { color: #888; }
  .pr-info-val { color: var(--d); font-weight: 600; }

  /* ALERT */
  .pr-alert { border-radius: 10px; padding: 12px 16px; font-size: 13.5px; line-height: 1.55; margin-bottom: 14px; }
  .pr-alert.success { background: rgba(23,59,52,0.08); border: 1px solid rgba(23,59,52,0.2); color: var(--g); }
  .pr-alert.error { background: rgba(220,38,38,0.06); border: 1px solid rgba(220,38,38,0.2); color: #B91C1C; }
  .pr-alert.warning { background: rgba(228,87,46,0.07); border: 1px solid rgba(228,87,46,0.2); color: #9A3A15; }
  .pr-alert.info { background: rgba(23,59,52,0.05); border: 1px solid var(--line); color: #555; }

  /* FORM */
  .pr-field { margin-bottom: 16px; }
  .pr-field label { font-size: 12px; font-weight: 600; color: #555; display: block; margin-bottom: 6px; }
  .pr-field input { width: 100%; padding: 11px 14px; border: 1.5px solid var(--line); border-radius: 10px; font-size: 14px; font-family: 'Inter', sans-serif; color: var(--d); background: #FDFCFA; outline: none; transition: border-color .15s; }
  .pr-field input:focus { border-color: var(--g); background: #fff; }

  /* BUTTONS */
  .pr-btn { padding: 11px 22px; border-radius: 999px; font-size: 13.5px; font-weight: 700; cursor: pointer; font-family: 'Inter', sans-serif; border: none; display: inline-flex; align-items: center; gap: 8px; transition: all .15s; }
  .pr-btn-green { background: var(--g); color: #F6F2EC; box-shadow: 0 4px 12px rgba(23,59,52,0.2); }
  .pr-btn-green:hover { background: #0E2820; }
  .pr-btn-green:disabled { opacity: .4; cursor: not-allowed; }
  .pr-btn-outline { background: #fff; color: var(--g); border: 1.5px solid var(--line); }
  .pr-btn-outline:hover { border-color: var(--g); }
  .pr-btn-orange { background: var(--o); color: #fff; box-shadow: 0 4px 12px rgba(228,87,46,0.25); }
  .pr-btn-orange:hover { opacity: .9; }
  .pr-btn-danger { background: #fff; color: #B91C1C; border: 1.5px solid rgba(220,38,38,0.3); }
  .pr-btn-danger:hover { background: rgba(220,38,38,0.05); }

  /* RGPD */
  .pr-right-item { display: flex; align-items: flex-start; gap: 12px; padding: 14px 0; border-bottom: 1px solid var(--line); }
  .pr-right-item:last-child { border-bottom: none; }
  .pr-right-ico { width: 36px; height: 36px; border-radius: 10px; background: rgba(23,59,52,0.07); display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
  .pr-right-title { font-size: 14px; font-weight: 700; color: var(--d); margin-bottom: 3px; }
  .pr-right-desc { font-size: 12.5px; color: #888; line-height: 1.55; }

  /* MESSAGES */
  .pr-msg-card { background: #fff; border: 1px solid var(--line); border-left: 3px solid var(--o); border-radius: 12px; padding: 16px 18px; margin-bottom: 10px; }
  .pr-msg-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
  .pr-msg-label { font-size: 11px; font-weight: 700; color: var(--o); text-transform: uppercase; letter-spacing: .06em; }
  .pr-msg-date { font-size: 11.5px; color: #B0A898; }
  .pr-msg-text { font-size: 14px; color: #444; line-height: 1.65; }

  /* DANGER */
  .pr-danger-card { border-color: rgba(220,38,38,0.25); }

  @media(max-width:640px) {
    .pr-body { padding: 20px 16px 48px; }
    .pr-nav { padding: 0 16px; }
    .pr-tab { font-size: 12px; padding: 8px 8px; }
  }
`;

export function ProfilPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('profil');

  // Mot de passe
  const [ancienPwd, setAncienPwd]   = useState('');
  const [nouveauPwd, setNouveauPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdMsg, setPwdMsg]         = useState(null);

  // Export
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMsg, setExportMsg]         = useState(null);

  // Suppression
  const [deletePwd, setDeletePwd]         = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteMsg, setDeleteMsg]         = useState(null);



  const changePwd = async (e) => {
    e.preventDefault();
    if (nouveauPwd !== confirmPwd) return setPwdMsg({ type: 'error', text: 'Les mots de passe ne correspondent pas.' });
    if (nouveauPwd.length < 8) return setPwdMsg({ type: 'error', text: 'Au moins 8 caractères requis.' });
    setPwdLoading(true);
    try {
      const res = await fetch('/api/auth/password', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('bs_token')}` },
        body: JSON.stringify({ ancienPassword: ancienPwd, nouveauPassword: nouveauPwd })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setPwdMsg({ type: 'success', text: 'Mot de passe mis à jour avec succès.' });
      setAncienPwd(''); setNouveauPwd(''); setConfirmPwd('');
    } catch (err) {
      setPwdMsg({ type: 'error', text: err.message });
    } finally { setPwdLoading(false); }
  };

  const exportData = async () => {
    setExportLoading(true);
    setExportMsg(null);
    try {
      const res = await fetch('/api/auth/export', {
        headers: { Authorization: `Bearer ${localStorage.getItem('bs_token')}` }
      });
      if (!res.ok) throw new Error('Erreur lors de l\'export.');
      const data = await res.json();
      // Télécharger en JSON
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `brandstarter-mes-donnees-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      setExportMsg({ type: 'success', text: 'Tes données ont été téléchargées.' });
    } catch (err) {
      setExportMsg({ type: 'error', text: err.message });
    } finally { setExportLoading(false); }
  };

  const deleteAccount = async () => {
    if (!deletePwd) return setDeleteMsg({ type: 'error', text: 'Mot de passe requis pour confirmer.' });
    setDeleteLoading(true);
    try {
      const res = await fetch('/api/auth/account', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('bs_token')}` },
        body: JSON.stringify({ password: deletePwd })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      logout();
      navigate('/');
    } catch (err) {
      setDeleteMsg({ type: 'error', text: err.message });
      setDeleteLoading(false);
    }
  };

  const initiales = `${user?.prenom?.[0] || ''}${user?.nom?.[0] || ''}`.toUpperCase();

  const TABS = [
    { id: 'profil',   label: '👤 Profil' },
    { id: 'securite', label: '🔒 Sécurité' },
    { id: 'rgpd',     label: '🛡️ Mes droits' },
  ];

  return (
    <>
      <style>{CSS}</style>
      <div className="pr">

        {/* NAV */}
        <nav className="pr-nav">
          <img src={logoBrandStarter} alt="BrandStarter" onClick={() => navigate('/parcours')}/>
          <div className="pr-nav-right">
            <button className="pr-nav-back" onClick={() => navigate('/parcours')}>← Parcours</button>
          </div>
        </nav>

        {/* HERO */}
        <div className="pr-hero">
          <div style={{maxWidth:720,margin:'0 auto'}}>
            <div className="pr-avatar">{initiales}</div>
            <div className="pr-hero-name">{user?.prenom} {user?.nom}</div>
            <div className="pr-hero-email">{user?.email}</div>
            <div className="pr-hero-badges">
              <span className="pr-badge pr-badge-green">{user?.isAdmin ? '⚙️ Admin' : '🌱 Utilisateur'}</span>
              <span className={`pr-badge ${user?.premiumDebloque ? 'pr-badge-gold' : 'pr-badge-green'}`}>
                {user?.premiumDebloque ? '⭐ Premium' : '🆓 Gratuit'}
              </span>
              {!user?.isAdmin && <span className="pr-badge pr-badge-orange">{user?.joursRestants > 0 ? `${user.joursRestants}j restants` : 'Accès expiré'}</span>}
            </div>
          </div>
        </div>

        <div className="pr-body">

          {/* TABS */}
          <div className="pr-tabs">
            {TABS.map(t => (
              <button key={t.id} className={`pr-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ── PROFIL ── */}
          {tab === 'profil' && (
            <div>
              <div className="pr-card">
                <div className="pr-card-title">Mes informations</div>
                {[
                  { lbl: 'Prénom', val: user?.prenom },
                  { lbl: 'Nom', val: user?.nom },
                  { lbl: 'Email', val: user?.email },
                  { lbl: 'Statut', val: user?.premiumDebloque ? '⭐ Premium' : '🆓 Gratuit' },
                  { lbl: 'Jours restants', val: user?.isAdmin ? 'Illimité' : `${user?.joursRestants ?? 0} jours` },
                ].map((info, i) => (
                  <div key={i} className="pr-info-row">
                    <span className="pr-info-lbl">{info.lbl}</span>
                    <span className="pr-info-val">{info.val}</span>
                  </div>
                ))}
              </div>
              <div className="pr-alert info">
                Pour modifier tes informations (prénom, nom, email), contacte Stratvente à{' '}
                <a href="mailto:commercial@stratvente.com" style={{color:'var(--g)',fontWeight:600}}>commercial@stratvente.com</a>
              </div>
            </div>
          )}

          {/* ── SÉCURITÉ ── */}
          {tab === 'securite' && (
            <div className="pr-card">
              <div className="pr-card-title">Changer mon mot de passe</div>
              {pwdMsg && <div className={`pr-alert ${pwdMsg.type}`}>{pwdMsg.text}</div>}
              <form onSubmit={changePwd} style={{display:'flex',flexDirection:'column',gap:14}}>
                <div className="pr-field">
                  <label>Mot de passe actuel</label>
                  <input type="password" value={ancienPwd} onChange={e => setAncienPwd(e.target.value)} placeholder="••••••••" required/>
                </div>
                <div className="pr-field">
                  <label>Nouveau mot de passe</label>
                  <input type="password" value={nouveauPwd} onChange={e => setNouveauPwd(e.target.value)} placeholder="8 caractères minimum" required/>
                </div>
                <div className="pr-field">
                  <label>Confirmer le nouveau mot de passe</label>
                  <input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} placeholder="••••••••" required/>
                </div>
                <button type="submit" className="pr-btn pr-btn-green" disabled={pwdLoading}>
                  {pwdLoading ? <Spinner size={16}/> : 'Mettre à jour'}
                </button>
              </form>
            </div>
          )}

          {/* ── RGPD / MES DROITS ── */}
          {tab === 'rgpd' && (
            <div>
              {/* Droits */}
              <div className="pr-card">
                <div className="pr-card-title">🛡️ Tes droits RGPD</div>
                {[
                  { ico:'🔍', t:'Droit d\'accès', d:'Tu peux télécharger l\'ensemble des données personnelles que BrandStarter détient sur toi.' },
                  { ico:'📦', t:'Droit à la portabilité', d:'Tes données t\'appartiennent. Tu peux les exporter dans un format lisible à tout moment.' },
                  { ico:'🗑️', t:'Droit à l\'effacement', d:'Tu peux demander la suppression complète de ton compte et de toutes tes données.' },
                  { ico:'✏️', t:'Droit de rectification', d:'Pour corriger tes informations, contacte-nous à commercial@stratvente.com.' },
                ].map((r, i) => (
                  <div key={i} className="pr-right-item">
                    <div className="pr-right-ico">{r.ico}</div>
                    <div>
                      <div className="pr-right-title">{r.t}</div>
                      <div className="pr-right-desc">{r.d}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Export */}
              <div className="pr-card">
                <div className="pr-card-title">📦 Exporter mes données</div>
                <p style={{fontSize:14,color:'#666',lineHeight:1.65,marginBottom:18}}>
                  Télécharge un fichier JSON contenant toutes les données personnelles que BrandStarter détient sur ton compte : informations de profil, progression, réponses aux modules.
                </p>
                {exportMsg && <div className={`pr-alert ${exportMsg.type}`}>{exportMsg.text}</div>}
                <button className="pr-btn pr-btn-orange" onClick={exportData} disabled={exportLoading}>
                  {exportLoading ? <Spinner size={16}/> : '⬇️ Télécharger mes données'}
                </button>
              </div>

              {/* Suppression */}
              <div className={`pr-card pr-danger-card`} style={{borderColor:'rgba(220,38,38,0.25)'}}>
                <div className="pr-card-title" style={{color:'#B91C1C'}}>🗑️ Supprimer mon compte</div>
                <div className="pr-alert warning" style={{marginBottom:18}}>
                  <strong>Action irréversible.</strong> La suppression efface définitivement ton compte, ta progression et toutes tes réponses aux modules. Tes données seront supprimées dans les 30 jours conformément à notre politique de confidentialité.
                </div>
                {deleteMsg && <div className={`pr-alert ${deleteMsg.type}`}>{deleteMsg.text}</div>}
                {!deleteConfirm ? (
                  <button className="pr-btn pr-btn-danger" onClick={() => setDeleteConfirm(true)}>
                    Supprimer mon compte
                  </button>
                ) : (
                  <div style={{display:'flex',flexDirection:'column',gap:14}}>
                    <div className="pr-field">
                      <label>Confirme avec ton mot de passe</label>
                      <input type="password" value={deletePwd} onChange={e => setDeletePwd(e.target.value)} placeholder="••••••••"/>
                    </div>
                    <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                      <button className="pr-btn pr-btn-danger" onClick={deleteAccount} disabled={deleteLoading}>
                        {deleteLoading ? <Spinner size={16}/> : 'Confirmer la suppression'}
                      </button>
                      <button className="pr-btn pr-btn-outline" onClick={() => { setDeleteConfirm(false); setDeletePwd(''); }}>
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Contact CNIL */}
              <div className="pr-alert info">
                Pour toute question relative à tes données ou pour exercer tes droits, contacte{' '}
                <a href="mailto:commercial@stratvente.com" style={{color:'var(--g)',fontWeight:600}}>commercial@stratvente.com</a>.
                En cas de litige, tu peux saisir la <a href="https://www.cnil.fr" target="_blank" rel="noreferrer" style={{color:'var(--g)',fontWeight:600}}>CNIL</a>.
              </div>
            </div>
          )}

        </div>

        {/* FOOTER */}
        <div style={{textAlign:'center',padding:'16px 24px 28px',fontSize:12,color:'#B0A898',display:'flex',alignItems:'center',justifyContent:'center',gap:16,flexWrap:'wrap',borderTop:'1px solid var(--line)',background:'#fff'}}>
          <span>© 2026 Stratvente · BrandStarter</span>
          <a href="/mentions-legales" style={{color:'#B0A898',textDecoration:'none'}}>Mentions légales</a>
          <a href="/confidentialite" style={{color:'#B0A898',textDecoration:'none'}}>Politique de confidentialité</a>
        </div>

        <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 20px 28px' }}>
          <button
            onClick={() => { logout(); navigate('/connexion'); }}
            style={{ width: '100%', minHeight: 54, borderRadius: 14, border: '1.5px solid #f1c3b8', background: '#fff', color: '#b8391a', fontWeight: 700, fontSize: 16, fontFamily: 'inherit', cursor: 'pointer' }}
          >
            Se déconnecter
          </button>
        </div>
        <BottomNav />
      </div>
    </>
  );
}
